/**
 * Konto ↔ Mitarbeiter (AC-107-1) — der Namensvorschlag beim Verknüpfen.
 *
 * Konto und Mitarbeiter haben keinen gemeinsamen Schlüssel: das Konto trägt die
 * Entra-`oid`, Anzeigename und E-Mail, der Mitarbeiter nur einen Namen. Ein
 * Vorschlag ist deshalb nur ein Vorschlag (E2): Er wird angezeigt und erst mit
 * einem Klick der Systemadministration zur Verknüpfung — nie automatisch, weil
 * zwei Personen gleich heißen können.
 *
 * Transportfrei (ADR-04) und OHNE Zod: die Oberfläche lädt die Datei über
 * `@asas/domain/konto-mitarbeiter`.
 */

/** Ein Mitarbeiter, wie die Nutzerverwaltung ihn zur Auswahl bekommt. */
export interface MitarbeiterKandidat {
  readonly id: string;
  readonly name: string;
  /** Das Konto, mit dem die Person schon verknüpft ist, sonst `null`. */
  readonly verknuepftMit: string | null;
}

/**
 * Vergleichsform eines Namens: Unicode-NFC, Leerraum zusammengefasst und
 * getrimmt, Groß- und Kleinschreibung egal. Mehr nicht — „Müller“ und
 * „Mueller“ bleiben verschieden, sonst schlüge der Vorschlag Falsche vor.
 */
export function namensSchluessel(name: string): string {
  return name.normalize('NFC').trim().replace(/\s+/gu, ' ').toLocaleLowerCase('de-DE');
}

/**
 * Der vorgeschlagene Mitarbeiter für ein Konto (E2): genau ein freier Kandidat
 * mit demselben Namen wie der Anzeigename aus Entra. Bei keinem, mehreren
 * (Namensgleichheit) oder einem schon anderweitig verknüpften Treffer kein
 * Vorschlag.
 */
export function mitarbeiterVorschlag<T extends MitarbeiterKandidat>(
  konto: { readonly id: string; readonly displayName: string | null },
  kandidaten: readonly T[],
): T | null {
  if (konto.displayName === null || namensSchluessel(konto.displayName) === '') return null;

  const schluessel = namensSchluessel(konto.displayName);
  const treffer = kandidaten.filter((kandidat) => namensSchluessel(kandidat.name) === schluessel);

  if (treffer.length !== 1) return null;

  const [einziger] = treffer as [T];
  return einziger.verknuepftMit === null || einziger.verknuepftMit === konto.id ? einziger : null;
}
